import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Fade,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useRecoilValue } from "recoil";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import ViewListIcon from "@mui/icons-material/ViewList";
import organizationIdSelector from "../../../../../atoms/organizationIdSelector";
import { useOrganizationDataByOrgId } from "../../../../../hooks/useOrganizationData";

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  marginBottom: theme.spacing(3),
}));

const CarouselBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  position: "relative",
});

const NavigationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: theme.spacing(1.5),
  flexShrink: 0,
}));

const QuickNavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  minWidth: "24px",
  height: "24px",
  padding: 0,
  borderRadius: "50%",
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.grey[200],
  color: active
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.dark
      : theme.palette.grey[300],
  },
}));

const QuestionTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
}));

const AnswerTypography = styled(Typography)({
  whiteSpace: "pre-wrap",
  marginBottom: 0,
});

const ContentContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const ListViewItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const ExtraDataQuestionsCard = ({ report }) => {
  const { extraData } = report;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState("carousel");
  const organizationId = useRecoilValue(organizationIdSelector);
  const { organizationData, loading } = useOrganizationDataByOrgId({
    organizationId,
  });

  // Load preference from localStorage on component mount
  useEffect(() => {
    const savedViewMode = localStorage.getItem("extraDataQuestionsViewMode");
    if (savedViewMode) {
      setViewMode(savedViewMode);
    }
  }, []);

  // Save preference to localStorage whenever viewMode changes
  useEffect(() => {
    localStorage.setItem("extraDataQuestionsViewMode", viewMode);
  }, [viewMode]);

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Match question responses with organization's weekly optional questions
  // Note: extraData.optionals contains weekly question responses keyed by question ID
  // IMPORTANT: This hook must be called before any conditional returns
  const matchedQuestions = useMemo(() => {
    // Early return if no optionals data
    if (
      !extraData ||
      !extraData.optionals ||
      Object.keys(extraData.optionals).length === 0
    ) {
      return [];
    }

    // Use weeklyOptional array for weekly questions (not optional array which is for daily)
    const organizationQuestions = organizationData?.weeklyOptional || organizationData?.optional;
    
    if (!organizationQuestions || !Array.isArray(organizationQuestions)) {
      // If organization data not loaded yet, return empty array
      return [];
    }

    // Match responses with question definitions from organization
    return Object.entries(extraData.optionals)
      .map(([questionId, responseData]) => {
        // Find the question definition from organization's weeklyOptional array
        const questionDef = organizationQuestions.find(
          (q) => q.id === questionId
        );

        if (questionDef) {
          // Use question definition (name, subTitle, index) with response data (value, notes)
          // Always prioritize the organization question definition's name property
          const questionName = questionDef.name || questionDef.subTitle || `Question ${questionId}`;
          return {
            // Response data first (value, notes, etc.)
            ...responseData,
            // Override with organization question definition data
            id: questionId,
            name: questionName,
            subTitle: questionDef.subTitle || "",
            index: questionDef.index !== undefined ? questionDef.index : 999,
          };
        }

        // Fallback if question definition not found
        return {
          id: questionId,
          name: responseData.name || responseData.subTitle || `Question ${questionId}`,
          subTitle: responseData.subTitle || "",
          index: 999,
          ...responseData,
        };
      })
      .sort((a, b) => {
        // Sort by index from organization questions
        if (a.index !== undefined && b.index !== undefined) {
          return a.index - b.index;
        }
        return 0;
      });
  }, [extraData?.optionals, organizationData]);

  // If no extraData or optionals, return null
  if (
    !extraData ||
    !extraData.optionals ||
    Object.keys(extraData.optionals).length === 0
  ) {
    return null;
  }

  // Wait for organization data to load before displaying questions
  // This prevents showing question IDs as names during loading
  if (loading || !organizationData) {
    return (
      <SectionCard>
        <CardContent sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={0.5}
          >
            <Typography variant="h5" fontWeight="bold">
              Weekly Check-in Responses
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 2,
              py: 3,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading questions...
            </Typography>
          </Box>
        </CardContent>
      </SectionCard>
    );
  }

  // Use matched questions instead of sortedOptionals
  const sortedOptionals = matchedQuestions;

  // If no matched questions after loading, return null
  if (!sortedOptionals || sortedOptionals.length === 0) {
    return null;
  }

  const totalQuestions = sortedOptionals.length;
  const currentQuestion = sortedOptionals[currentIndex];

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalQuestions);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalQuestions) % totalQuestions);
  };
  const handleQuickNav = (index) => {
    setCurrentIndex(index);
  };

  // Render carousel view for a single question
  const renderCarouselView = () => {
    // Use name from organization question definition
    const questionTitle = currentQuestion.name || `Question ${currentIndex + 1}`;
    const questionSubtitle = currentQuestion.subTitle || "";
    const answer =
      currentQuestion.value !== undefined
        ? currentQuestion.value.toString()
        : "No response";

    return (
      <CarouselBox>
        <ContentContainer>
          <Typography variant="h6" fontWeight={600} mb={0.5}>
            {questionTitle}
          </Typography>

          {questionSubtitle && (
            <QuestionTypography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {questionSubtitle}
            </QuestionTypography>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              bgcolor: (theme) => theme.palette.grey[50],
              mb: currentQuestion.notes ? 1 : 0,
              borderRadius: 1.5,
            }}
          >
            <AnswerTypography variant="body1">{answer}</AnswerTypography>
          </Paper>

          {/* Display notes if available */}
          {currentQuestion.notes && (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
              sx={{ mt: 0.5 }}
            >
              Note: {currentQuestion.notes}
            </Typography>
          )}
        </ContentContainer>

        <NavigationContainer>
          <IconButton onClick={handlePrev} color="primary">
            <ArrowBackIosNewIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                justifyContent: "center",
              }}
            >
              {sortedOptionals.map((item, idx) => (
                <Tooltip
                  key={item.id}
                  title={item.name || `Question ${idx + 1}`}
                  TransitionComponent={Fade}
                  arrow
                  placement="top"
                >
                  <QuickNavButton
                    active={idx === currentIndex}
                    onClick={() => handleQuickNav(idx)}
                    variant={idx === currentIndex ? "contained" : "outlined"}
                  >
                    {idx + 1}
                  </QuickNavButton>
                </Tooltip>
              ))}
            </Box>
          </Box>

          <IconButton onClick={handleNext} color="primary">
            <ArrowForwardIosIcon />
          </IconButton>
        </NavigationContainer>
      </CarouselBox>
    );
  };

  // Render list view showing all questions and answers
  const renderListView = () => {
    return (
      <Box>
        {sortedOptionals.map((questionItem, index) => {
          // Use name from organization question definition
          const questionTitle = questionItem.name || `Question ${index + 1}`;
          const questionSubtitle = questionItem.subTitle || "";
          const answer =
            questionItem.value !== undefined
              ? questionItem.value.toString()
              : "No response";

          return (
            <ListViewItem key={questionItem.id}>
              <Typography variant="subtitle1" fontWeight={600} mb={0.25}>
                {index + 1}. {questionTitle}
              </Typography>

              {questionSubtitle && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.75 }}
                >
                  {questionSubtitle}
                </Typography>
              )}

              <Paper
                elevation={0}
                sx={{
                  p: 1.25,
                  bgcolor: (theme) => theme.palette.grey[50],
                  mb: questionItem.notes ? 0.5 : 0,
                  borderRadius: 1.5,
                }}
              >
                <AnswerTypography variant="body1">{answer}</AnswerTypography>
              </Paper>

              {questionItem.notes && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                  sx={{ mt: 0.5 }}
                >
                  Note: {questionItem.notes}
                </Typography>
              )}

              {index < sortedOptionals.length - 1 && (
                <Divider sx={{ mt: 1.5 }} />
              )}
            </ListViewItem>
          );
        })}
      </Box>
    );
  };

  return (
    <SectionCard>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
          mb={0.5}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Weekly Check-in Responses
            </Typography>
            {viewMode === "carousel" && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.25, mb: 0 }}>
                {currentIndex + 1} of {totalQuestions}
              </Typography>
            )}
          </Box>

          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
          >
            <ToggleButton value="carousel" aria-label="carousel view">
              <Tooltip title="Carousel View">
                <ViewCarouselIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <Tooltip title="List View">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mb: 2, mt: 1 }} />

        {viewMode === "carousel" ? renderCarouselView() : renderListView()}
      </CardContent>
    </SectionCard>
  );
};

export default ExtraDataQuestionsCard;
